import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, switchMap, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  EmlPathResponse,
  MailAccountForSearch,
  MailApiResponse,
  MailHit,
  MailsApiResponse,
  MeMailAccountsResponse,
  MailSearchParams,
  MailSearchResult,
} from '../../models/mailarchiv/mail.model';
import { ApiEnvelope } from '../../helpers/api-envelope.helper';

@Injectable({
  providedIn: 'root',
})
export class MailService {
  constructor(private http: HttpClient) {}

  search(params: MailSearchParams): Observable<MailSearchResult> {
    return this.http
      .post<MailsApiResponse | ApiEnvelope<MailsApiResponse>>(environment.apiUrl + '/mails', params)
      .pipe(
        map((res) => {
          const payload = 'data' in res ? res.data : res;
          const m = payload.mails;
          return {
            hits: m.hits ?? [],
            estimatedTotalHits: m.estimatedTotalHits ?? 0,
            limit: m.limit ?? 25,
            offset: m.offset ?? 0,
          };
        })
      );
  }

  /** Mail accounts the current user can search (for filter dropdown). */
  getMailAccountsForSearch(): Observable<MailAccountForSearch[]> {
    return this.http
      .get<MeMailAccountsResponse>(environment.apiUrl + '/me/mail-accounts')
      .pipe(map((res) => res.data ?? []));
  }

  getOne(id: string): Observable<MailHit | null> {
    return this.http.get<MailApiResponse | ApiEnvelope<MailApiResponse>>(environment.apiUrl + '/mails/' + id).pipe(
      map((res) => ('data' in res ? res.data : res).mail?.hits?.[0] ?? null)
    );
  }

  getEmlPath(id: string): Observable<{ filename: string }> {
    return this.http.get<EmlPathResponse | ApiEnvelope<EmlPathResponse>>(
      environment.apiUrl + '/mails/' + id + '/eml.gz/path'
    ).pipe(
      map((res) => {
        const payload = 'data' in res ? res.data : res;
        return { filename: payload.filename ?? 'message.eml.gz' };
      })
    );
  }

  /** Ensure download filename has .eml or .eml.gz so the OS recognizes the type. */
  private ensureEmlExtension(name: string): string {
    const n = (name || '').trim() || 'message';
    if (n.endsWith('.eml.gz') || n.endsWith('.eml')) return n;
    return n + '.eml.gz';
  }

  downloadEml(id: string): Observable<Blob> {
    return this.getEmlPath(id).pipe(
      switchMap(({ filename }) =>
        this.http.get(environment.apiUrl + '/mails/' + id + '/eml.gz', {
          responseType: 'blob',
        }).pipe(
          tap((blob) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = this.ensureEmlExtension(filename);
            a.click();
            window.URL.revokeObjectURL(url);
          })
        )
      )
    );
  }

  downloadAttachment(mailId: string, attachmentName: string): Observable<Blob> {
    const url =
      environment.apiUrl +
      '/mails/' +
      encodeURIComponent(mailId) +
      '/attachments/' +
      encodeURIComponent(attachmentName);
    return this.http.get(url, { responseType: 'blob' }).pipe(
      tap((blob) => {
        const urlObj = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlObj;
        a.download = attachmentName;
        a.click();
        window.URL.revokeObjectURL(urlObj);
      })
    );
  }
}
